/**
 * Format a Date object to YYYY-MM-DD string without timezone conversion
 * This prevents the common issue where toISOString() converts to UTC and changes the day
 *
 * @param date - The date to format
 * @returns Date string in YYYY-MM-DD format
 *
 * @example
 * const date = new Date(2025, 7, 1); // Aug 1, 2025 (month is 0-indexed)
 * formatDateForAPI(date); // Returns "2025-08-01"
 */
export function formatDateForAPI(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Format a Date object to ISO string (YYYY-MM-DDTHH:mm:ss.sssZ)
 * Use this only when you need the full timestamp with timezone
 * For date-only parameters, use formatDateForAPI instead
 */
export function formatDateTimeForAPI(date: Date): string {
  return date.toISOString();
}

/**
 * Format a date string (YYYY-MM-DD or ISO format) to localized date string
 * without timezone conversion issues
 *
 * @param dateString - Date string in YYYY-MM-DD or ISO format
 * @returns Formatted date string (e.g., "28/02/1904" for pt-PT locale)
 *
 * @example
 * formatDateForDisplay("1904-02-28"); // Returns "28/02/1904" (pt-PT)
 * formatDateForDisplay("2025-08-01T00:00:00Z"); // Returns "01/08/2025" (pt-PT)
 *
 * @deprecated Use formatDateInUserTimezone() with useDateFormatter() hook instead
 * This function doesn't account for user's timezone preference
 */
export function formatDateForDisplay(dateString: string | null | undefined): string {
  if (process.env.NODE_ENV === 'development') {
    console.warn(
      'formatDateForDisplay() is deprecated. Use useDateFormatter() hook instead for timezone-aware formatting.'
    );
  }

  if (!dateString) return 'N/A';

  // Extract just the date part (YYYY-MM-DD) to avoid timezone issues
  const datePart = dateString.split('T')[0];
  const [year, month, day] = datePart.split('-');

  // Create date using local timezone
  const date = new Date(Number(year), Number(month) - 1, Number(day));

  return date.toLocaleDateString();
}

// ============================================
// TIMEZONE-AWARE DATE FORMATTING
// ============================================

import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import { ptBR, enUS, es } from 'date-fns/locale';

/**
 * Converts UTC date string to user's timezone
 * @param utcDateString - ISO 8601 string from API (e.g., "2025-01-08T14:30:00Z")
 * @param userTimezone - IANA timezone (e.g., "Europe/Lisbon", "America/New_York")
 * @returns Date object in user's timezone
 *
 * @example
 * convertUTCToUserTimezone("2025-01-08T12:00:00Z", "Europe/Lisbon")
 * // Returns Date object for 08/01/2025 12:00 in Lisbon time
 */
export function convertUTCToUserTimezone(
  utcDateString: string | null | undefined,
  userTimezone: string = 'UTC'
): Date | null {
  if (!utcDateString) return null;

  try {
    // Parse UTC date string
    const utcDate = new Date(utcDateString);

    // Convert to user's timezone
    const zonedDate = toZonedTime(utcDate, userTimezone);

    return zonedDate;
  } catch (error) {
    console.error('Error converting UTC to user timezone:', error);
    return null;
  }
}

/**
 * Formats UTC date string for display in user's timezone
 * @param utcDateString - ISO 8601 string from API
 * @param userTimezone - IANA timezone
 * @param formatString - date-fns format string (default: 'PPp')
 * @param locale - User locale (default: 'pt-BR')
 * @returns Formatted date string in user's timezone
 *
 * Format examples:
 * - 'PP' → Jan 8, 2025
 * - 'PPp' → Jan 8, 2025, 2:30 PM
 * - 'PPpp' → Jan 8, 2025, 2:30:45 PM GMT+1
 * - 'dd/MM/yyyy' → 08/01/2025
 * - 'dd/MM/yyyy HH:mm' → 08/01/2025 14:30
 *
 * @example
 * formatDateInUserTimezone("2025-01-08T12:00:00Z", "Europe/Lisbon", "dd/MM/yyyy HH:mm")
 * // Returns "08/01/2025 12:00"
 */
export function formatDateInUserTimezone(
  utcDateString: string | null | undefined,
  userTimezone: string = 'UTC',
  formatString: string = 'PPp',
  locale: 'pt-BR' | 'en-US' | 'es-ES' = 'pt-BR'
): string {
  if (!utcDateString) return '-';

  try {
    const utcDate = new Date(utcDateString);
    const localeObj = locale === 'pt-BR' ? ptBR : locale === 'es-ES' ? es : enUS;

    // Format date in user's timezone
    return formatInTimeZone(utcDate, userTimezone, formatString, { locale: localeObj });
  } catch (error) {
    console.error('Error formatting date in user timezone:', error);
    return utcDateString; // Fallback to original string
  }
}

/**
 * Formats date for relative time (e.g., "2 hours ago") in user's timezone
 * @param utcDateString - ISO 8601 string from API
 * @param userTimezone - IANA timezone
 * @returns Relative time string in Portuguese
 *
 * @example
 * formatRelativeTimeInUserTimezone("2025-01-08T10:00:00Z", "Europe/Lisbon")
 * // If now is 12:00, returns "2h atrás"
 */
export function formatRelativeTimeInUserTimezone(
  utcDateString: string | null | undefined,
  userTimezone: string = 'UTC'
): string {
  if (!utcDateString) return '-';

  try {
    const utcDate = new Date(utcDateString);
    const zonedDate = toZonedTime(utcDate, userTimezone);
    const now = new Date();

    const diffMs = now.getTime() - zonedDate.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMinutes < 1) return 'agora';
    if (diffMinutes < 60) return `${diffMinutes}m atrás`;
    if (diffHours < 24) return `${diffHours}h atrás`;
    if (diffDays < 7) return `${diffDays}d atrás`;

    // For older dates, show formatted date
    return formatDateInUserTimezone(utcDateString, userTimezone, 'dd/MM/yyyy');
  } catch (error) {
    console.error('Error formatting relative time:', error);
    return utcDateString;
  }
}
